import {
  useEffect,
  useMemo,
  useRef,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from "react";
import type { BootstrapPayload } from "../../shared/contracts/bootstrap.js";
import type {
  RoundConflictSnapshot,
  RoundSnapshot,
} from "../../domain/round/round-reducer.js";
import { roundJournal } from "../data/local-database.js";
import {
  createRoundAutoSync,
  type RoundAutoSync,
} from "../sync/round-auto-sync.js";
import type { RoundSyncEngine } from "../sync/round-sync-engine.js";

/** What every round workspace action gets: state, setters and the engine. */
export interface ActionContext {
  accountId: string;
  authenticated: boolean;
  online: boolean;
  bootstrap: BootstrapPayload | null;
  snapshot: RoundSnapshot | null;
  conflicts: RoundConflictSnapshot[];
  engine: RoundSyncEngine;
  autoSync: RoundAutoSync;
  setSnapshot: Dispatch<SetStateAction<RoundSnapshot | null>>;
  setQueued: Dispatch<SetStateAction<boolean>>;
  setConflicted: Dispatch<SetStateAction<boolean>>;
  setConflicts: Dispatch<SetStateAction<RoundConflictSnapshot[]>>;
  setMessage: Dispatch<SetStateAction<string>>;
}

/** Scoring commands reach the server this long after the last tap. */
const AUTO_SYNC_DELAY_MS = 1000;

/**
 * One scheduler per engine: switching accounts settles the old one so a
 * late flush never runs against another account's round. The returned ref
 * must point at the current context before the first flush can fire.
 */
export function useAutoSync(
  engine: RoundSyncEngine,
): [RoundAutoSync, RefObject<ActionContext | null>] {
  const contextRef = useRef<ActionContext | null>(null);
  const autoSync = useMemo(
    () =>
      createRoundAutoSync(
        () => autoFlush(contextRef.current),
        AUTO_SYNC_DELAY_MS,
      ),
    [engine],
  );
  useEffect(
    () => () => {
      void autoSync.settle();
    },
    [autoSync],
  );
  return [autoSync, contextRef];
}

/** Offline or unverified scoring stays queued; the workspace retries on reconnection. */
export function scheduleAutoSync(context: ActionContext): void {
  if (context.authenticated && context.online) context.autoSync.schedule();
}

async function autoFlush(context: ActionContext | null): Promise<void> {
  if (!context?.snapshot || !context.authenticated || !context.online) return;
  try {
    await pushRound(context, context.snapshot.roundId);
  } catch (error) {
    context.setMessage(errorMessage(error));
  }
}

export async function syncRound(context: ActionContext): Promise<void> {
  if (!context.authenticated) {
    throw new Error("Sign in again to sync saved changes.");
  }
  const snapshot = requireRound(context);
  context.setMessage("");
  await context.autoSync.settle();
  await pushRound(context, snapshot.roundId);
}

async function pushRound(
  context: ActionContext,
  roundId: string,
): Promise<void> {
  context.setSnapshot(await context.engine.flush(roundId));
  const conflicts = context.engine.getConflicts();
  context.setQueued(await roundJournal.hasUnsynced(context.accountId));
  context.setConflicted(conflicts.length > 0);
  context.setConflicts(conflicts);
  context.setMessage(
    conflicts.length > 0
      ? "A score conflict needs review. Your change is safe on the server."
      : "",
  );
}

export async function finishRound(context: ActionContext): Promise<void> {
  const snapshot = requireRound(context);
  if (context.conflicts.length > 0) {
    throw new Error("Resolve the score conflict before you finish.");
  }
  if (context.authenticated && context.online) {
    await context.autoSync.settle();
    await pushRound(context, snapshot.roundId);
    if (context.engine.getConflicts().length)
      throw new Error("Resolve the score conflict before you finish.");
  }
  context.setMessage("Saving the round finish on this device…");
  context.setSnapshot(await context.engine.finishRound(snapshot.roundId));
  context.setQueued(true);
  if (!context.authenticated || !context.online) {
    context.setMessage(
      context.authenticated
        ? "Saved on this device. Your result will be shared automatically when connected."
        : "Saved on this device. Reconnect to your account to share the result.",
    );
    return;
  }
  await pushRound(context, snapshot.roundId);
  if (context.engine.getConflicts().length)
    throw new Error("Resolve the score conflict before you finish.");
  context.setMessage("Round complete. The result is saved.");
}

export async function abandonRound(context: ActionContext): Promise<void> {
  const snapshot = requireRound(context);
  if (context.conflicts.length > 0) {
    throw new Error(
      "Resolve the score conflict before you abandon this round.",
    );
  }
  context.setSnapshot(await context.engine.abandonRound(snapshot.roundId));
  context.setQueued(true);
  context.setMessage("");
  if (!context.authenticated || !context.online) {
    return;
  }
  await context.autoSync.settle();
  await pushRound(context, snapshot.roundId);
  if (context.engine.getConflicts().length)
    throw new Error(
      "The round could not be abandoned because a score conflict needs review.",
    );
  context.setMessage("");
}

export async function transferScorekeeper(
  context: ActionContext,
  nextAccountId: string,
): Promise<void> {
  if (!context.authenticated || !context.online)
    throw new Error("Connect and sign in before transferring scorekeeping.");
  if (context.conflicts.length)
    throw new Error("Resolve the score conflict before transferring.");
  const snapshot = requireRound(context);
  await context.autoSync.settle();
  await pushRound(context, snapshot.roundId);
  try {
    if (context.engine.getConflicts().length)
      throw new Error("Resolve the score conflict before transferring.");
    context.setSnapshot(
      await context.engine.transferScorekeeper(snapshot.roundId, nextAccountId),
    );
    context.setMessage(
      "Scorekeeping transferred. You can follow the round here.",
    );
  } finally {
    context.setQueued(await roundJournal.hasUnsynced(context.accountId));
  }
}

export function requireRound(context: ActionContext): RoundSnapshot {
  if (!context.snapshot) throw new Error("The active round is not available.");
  return context.snapshot;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "The request failed.";
}
